import { c as _c } from "react/compiler-runtime";
import { useCustomQuery } from './useCustomQuery';
export interface GeoAreaData {
  id: string;
  region: string;
  suburbs: [string, string, string, string, string][]; // [postcode, name, lng, lat, district]
}
export interface GeoArea {
  postcode: string;
  name: string;
  region: string;
  regionId: string;
}
export function useGeoAreasNZ() {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["geo-areas-nz"],
      queryFn: _temp,
      staleTime: Infinity
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: t1,
    isLoading,
    error
  } = useCustomQuery(t0);
  let t2;
  if ($[1] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const regionsData = t2;
  let t3;
  if ($[3] !== error || $[4] !== isLoading || $[5] !== regionsData) {
    t3 = {
      regionsData,
      isLoading,
      error
    };
    $[3] = error;
    $[4] = isLoading;
    $[5] = regionsData;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * Helper function to find a geo area by its ID
 * @param geoAreaId - Format: "regionId-postcode-geoAreaName"
 * @param regionsData - The regions data array
 * @returns The geo area name or null if not found
 */
async function _temp() {
  const response = await fetch("/nz-region-suburbs.json");
  if (!response.ok) {
    throw new Error("Failed to fetch geo areas data");
  }
  return response.json();
}
export function findGeoAreaById(geoAreaId: string, regionsData: GeoAreaData[]): string | null {
  if (!geoAreaId || !regionsData.length) return null;

  // Parse the geo area ID format: "regionId-postcode-geoAreaName"
  const parts = geoAreaId.split('-');
  if (parts.length < 3) return null;
  const regionId = parts[0];
  const postcode = parts[1];
  const geoAreaName = parts.slice(2).join('-'); // Handle geo area names with dashes

  const region = regionsData.find(r => r.id === regionId);
  if (!region) return null;
  const geoArea = region.suburbs.find(([pc, name]) => pc === postcode && name === geoAreaName);
  return geoArea ? geoArea[1] : null;
}

/**
 * Helper function to resolve multiple geo area IDs to their names with postcodes.
 * Handles both region IDs (e.g., "Auckland") and area IDs (e.g., "Auckland:1010:Auckland Central").
 * @param geoAreaIds - Array of geo area or region IDs
 * @param regionsData - The regions data array
 * @returns Array of resolved names (region name or "areaName (postcode)")
 */
export function resolveGeoAreaNames(geoAreaIds: string[], regionsData: GeoAreaData[]): string[] {
  if (!geoAreaIds?.length || !regionsData.length) return [];
  return geoAreaIds.map(id => {
    const parts = id.split(':');

    // Region-only ID (no colons) — resolve to region name
    if (parts.length === 1) {
      const region = regionsData.find(r => r.region === id);
      return region ? region.region : null;
    }
    if (parts.length < 3) return null;
    const regionName = parts[0];
    const postcode = parts[1];
    const geoAreaName = parts[2];
    const region = regionsData.find(r => r.region === regionName);
    if (!region) return null;
    const geoArea = region.suburbs.find(([pc, name]) => pc === postcode && name === geoAreaName);
    if (!geoArea) return null;

    // Return geo area name with postcode
    return `${geoArea[1]} (${geoArea[0]})`;
  }).filter((name): name is string => name !== null);
}